import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { fetchWaterBodies, splitIntoSegments } from './utils/overpass';
import { makeLayer } from './simulation/engine';
import { computeWQI, wqiToColor, defaultParams } from './simulation/wqi';

const RIVER_SOURCE = 'wsim-rivers';
const LAKE_SOURCE  = 'wsim-lakes';
const RIVER_LAYER  = 'wsim-river-layer';
const LAKE_LAYER   = 'wsim-lake-layer';
const LAKE_OUTLINE = 'wsim-lake-outline';
const INDUSTRY_SOURCE = 'wsim-industries';
const INDUSTRY_LAYER  = 'wsim-industries-layer';

/**
 * Color expression driven by WQI feature-state (0–100, higher = cleaner).
 */
function colorExpr(c) {
  return [
    'step', ['coalesce', ['feature-state', 'wqi'], 100],
    c.severe,        // WQI < 40  → Critical / Poor
    40, c.moderate,  // WQI 40–59 → Fair
    60, c.mild,      // WQI 60–79 → Good
    80, c.clean,     // WQI ≥ 80  → Pristine
  ];
}

/**
 * Width expression: worse WQI → thicker line
 */
function widthExpr(base) {
  return [
    '+', base,
    ['*', ['-', 1, ['/', ['coalesce', ['feature-state', 'wqi'], 100], 100]], 6],
  ];
}

function toRad(x) { return (x * Math.PI) / 180; }

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function mapIndustryWaterToParams(water) {
  const base = defaultParams();
  if (!water) return base;
  const p = { ...base };
  if (water.ph != null) p.ph = water.ph;
  if (water.bod != null) p.bod = water.bod;
  if (water.cod != null) p.cod = water.cod;
  if (water.tss != null) p.turb = water.tss;
  if (water.turbidity != null) p.turb = water.turbidity;
  return p;
}

export default function MapView({
  config, layers, setLayers, onLayerClick,
  colorConfig, mapRef, fetchKey, onLoaded, onFetchError, 
  onMapReady, industries = [], mode = 'water', onMapClick
}) {
  const containerRef = useRef(null);
  const onLayerClickRef = useRef(onLayerClick);
  const colorConfigRef = useRef(colorConfig);
  const baseWidthRef = useRef(config.baseWidth);
  const opacityRef = useRef(config.opacity);
  const onMapClickRef = useRef(onMapClick);

  useEffect(() => { onLayerClickRef.current = onLayerClick; });
  useEffect(() => { colorConfigRef.current = colorConfig; });
  useEffect(() => { baseWidthRef.current = config.baseWidth; });
  useEffect(() => { opacityRef.current = config.opacity; });
  useEffect(() => { onMapClickRef.current = onMapClick; });

  // ── Create Map ──
  useEffect(() => {
    if (!config.token || mapRef.current) return;
    mapboxgl.accessToken = config.token;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [config.lng, config.lat],
      zoom: config.zoom,
      attributionControl: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');
    mapRef.current = map;
    
    if (onMapReady) map.once('load', () => onMapReady());
    
    return () => { map.remove(); mapRef.current = null; };
  }, [config.token]);

  // Global map click handler
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handler = (e) => {
      if (onMapClickRef.current) onMapClickRef.current(e.lngLat);
    };
    map.on('click', handler);
    return () => { map.off('click', handler); };
  }, [mapRef]);

  // ── Fetch + Render ──
  useEffect(() => {
    if (!fetchKey || !config.token || mode !== 'water') return;
    let cancelled = false;

    const run = async () => {
      const map = mapRef.current;
      if (!map) return;
      if (!map.isStyleLoaded()) await new Promise(resolve => map.once('load', resolve));
      if (cancelled) return;

      map.flyTo({ center: [config.lng, config.lat], zoom: config.zoom });

      // Clean up
      for (const id of [RIVER_LAYER, LAKE_LAYER, LAKE_OUTLINE]) {
        try { if (map.getLayer(id)) map.removeLayer(id); } catch (_) {}
      }
      for (const id of [RIVER_SOURCE, LAKE_SOURCE]) {
        try { if (map.getSource(id)) map.removeSource(id); } catch (_) {}
      }
      setLayers([]);

      let geojson;
      try {
        geojson = await fetchWaterBodies(config.bbox);
      } catch (e) {
        if (!cancelled) onFetchError(e.message);
        return;
      }
      if (cancelled) return;

      const riverFeatures = [];
      const lakeFeatures = [];
      const newLayers = [];
      let fid = 1;

      geojson.features.forEach(feature => {
        const geomType = feature.geometry.type;
        const name = feature.properties?.name || feature.properties?.waterway || 'Water body';

        if (geomType === 'LineString' || geomType === 'MultiLineString') {
          const lines = geomType === 'LineString' ? [feature] : 
            feature.geometry.coordinates.map(c => ({ type: 'Feature', geometry: { type: 'LineString', coordinates: c }, properties: feature.properties }));
          
          lines.forEach((lf, li) => {
            splitIntoSegments(lf, 8).forEach((seg, si) => {
              const id = fid++;
              const label = lines.length > 1 ? `${name} (pt ${li+1}, seg ${si+1})` : `${name} (seg ${si+1})`;
              riverFeatures.push({ ...seg, id });
              newLayers.push(makeLayer(id, 'river', label));
            });
          });
        }
        if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
          const id = fid++;
          lakeFeatures.push({ ...feature, id });
          newLayers.push(makeLayer(id, 'lake', name));
        }
      });

      // Industry Impact Logic
      if (industries.length > 0) {
        const centers = new Map();
        riverFeatures.forEach(f => {
          const c = f.geometry.coordinates;
          if (!c.length) return;
          let sLo = 0, sLa = 0;
          c.forEach(([lo, la]) => { sLo += lo; sLa += la; });
          centers.set(f.id, { lon: sLo/c.length, lat: sLa/c.length });
        });
        lakeFeatures.forEach(f => {
          let lo, la;
          const g = f.geometry;
          if (g.type === 'Polygon') [lo, la] = g.coordinates[0][0];
          else if (g.type === 'MultiPolygon') [lo, la] = g.coordinates[0][0][0];
          if (lo != null) centers.set(f.id, { lon: lo, lat: la });
        });

        const MAX_DIST = 4;
        newLayers.forEach(l => {
          const c = centers.get(l.id);
          if (!c) return;
          let near = null, b = Infinity;
          industries.forEach(i => {
            if (i.lat == null) return;
            const d = haversineKm(c.lat, c.lon, i.lat, i.lng);
            if (d < b) { b = d; near = i; }
          });
          if (near && b <= MAX_DIST && near.water_data) {
            const p = mapIndustryWaterToParams(near.water_data);
            const { wqi } = computeWQI(p);
            l.params = p; l.wqi = wqi; l.color = wqiToColor(wqi);
            l.industryId = near._id; l.industryName = near.name;
          }
        });
      }

      const colors = colorConfigRef.current;
      const base = baseWidthRef.current;
      const opacity = opacityRef.current;

      if (riverFeatures.length > 0) {
        map.addSource(RIVER_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: riverFeatures }});
        map.addLayer({
          id: RIVER_LAYER, type: 'line', source: RIVER_SOURCE,
          paint: { 'line-color': colorExpr(colors), 'line-width': widthExpr(base), 'line-opacity': opacity, 'line-cap': 'round', 'line-join': 'round' }
        });
        map.on('mouseenter', RIVER_LAYER, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', RIVER_LAYER, () => { map.getCanvas().style.cursor = ''; });
        map.on('click', RIVER_LAYER, e => {
          const f = e.features?.[0];
          if (f) onLayerClickRef.current({ id: f.id, type: 'river', lngLat: e.lngLat });
        });
      }
      if (lakeFeatures.length > 0) {
        map.addSource(LAKE_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: lakeFeatures }});
        map.addLayer({
          id: LAKE_LAYER, type: 'fill', source: LAKE_SOURCE,
          paint: {
            'fill-color': colorExpr(colors),
            'fill-opacity': ['*', opacity * 0.6, ['+', 1, ['*', 0.4, ['-', 1, ['/', ['coalesce', ['feature-state', 'wqi'], 100], 100]]]]],
          }
        });
        map.addLayer({ id: LAKE_OUTLINE, type: 'line', source: LAKE_SOURCE, paint: { 'line-color': colorExpr(colors), 'line-width': 1, 'line-opacity': 0.4 }});
        map.on('mouseenter', LAKE_LAYER, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', LAKE_LAYER, () => { map.getCanvas().style.cursor = ''; });
        map.on('click', LAKE_LAYER, e => {
          const f = e.features?.[0];
          if (f) onLayerClickRef.current({ id: f.id, type: 'lake', lngLat: e.lngLat });
        });
      }

      newLayers.forEach(({ id, type, wqi }) => {
        try { map.setFeatureState({ source: type === 'river' ? RIVER_SOURCE : LAKE_SOURCE, id }, { wqi }); } catch (_) {}
      });

      setLayers(newLayers);
      if (!cancelled) onLoaded(newLayers.length);
    };
    run();
    return () => { cancelled = true; };
  }, [fetchKey]);

  // Remove water layers in Air mode
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (mode !== 'air') return;
    [RIVER_LAYER, LAKE_LAYER, LAKE_OUTLINE].forEach(id => { try { if (map.getLayer(id)) map.removeLayer(id); } catch (_) {}});
    [RIVER_SOURCE, LAKE_SOURCE].forEach(id => { try { if (map.getSource(id)) map.removeSource(id); } catch (_) {}});
    setLayers([]);
  }, [mode, mapRef]);

  // Update states
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    layers.forEach(({ id, type, wqi }) => {
      try { map.setFeatureState({ source: type === 'river' ? RIVER_SOURCE : LAKE_SOURCE, id }, { wqi: wqi ?? 100 }); } catch (_) {}
    });
  }, [layers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const expr = colorExpr(colorConfig);
    try {
      if (map.getLayer(RIVER_LAYER)) map.setPaintProperty(RIVER_LAYER, 'line-color', expr);
      if (map.getLayer(LAKE_LAYER)) map.setPaintProperty(LAKE_LAYER, 'fill-color', expr);
      if (map.getLayer(LAKE_OUTLINE)) map.setPaintProperty(LAKE_OUTLINE, 'line-color', expr);
    } catch (_) {}
  }, [colorConfig]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    try {
      if (map.getLayer(RIVER_LAYER)) {
        map.setPaintProperty(RIVER_LAYER, 'line-width', widthExpr(config.baseWidth));
        map.setPaintProperty(RIVER_LAYER, 'line-opacity', config.opacity);
      }
      if (map.getLayer(LAKE_LAYER)) {
        map.setPaintProperty(LAKE_LAYER, 'fill-opacity', ['*', config.opacity * 0.6, ['+', 1, ['*', 0.4, ['-', 1, ['/', ['coalesce', ['feature-state', 'wqi'], 100], 100]]]]]);
      }
    } catch (_) {}
  }, [config.baseWidth, config.opacity]);

  // Industry markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    try {
      if (map.getLayer(INDUSTRY_LAYER)) map.removeLayer(INDUSTRY_LAYER);
      if (map.getSource(INDUSTRY_SOURCE)) map.removeSource(INDUSTRY_SOURCE);
    } catch (_) {}
    if (!industries?.length) return;
    const features = industries.filter(i => i.lng != null).map(i => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [i.lng, i.lat] }, properties: { id: i._id, name: i.name, type: i.industry_type }}));
    if (!features.length) return;
    map.addSource(INDUSTRY_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features }});
    map.addLayer({ id: INDUSTRY_LAYER, type: 'circle', source: INDUSTRY_SOURCE, paint: { 'circle-radius': 5, 'circle-color': '#f97316', 'circle-stroke-color': '#000000', 'circle-stroke-width': 1.5, 'circle-opacity': 0.9 }});
  }, [industries, mapRef]);

  return <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />;
}
